import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import {
  ArrowLeft, UserPlus, Loader2, Trash2, Copy, Mail, Crown, Users as UsersIcon,
  Shield, Building2, MapPin, Calendar, ArrowRightLeft,
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDate } from '../../utils/timeFormat';
// Iter-210 — Org Workspace UI.
// Single page combining: workspace overview (company name + my role +
// my scope), team members with role + scope chips, pending invites,
// and an invite-team-member modal that picks role + scope at invite time.
//
// We rely on:
//   GET   /client/workspace
//   GET   /client/workspace/members
//   POST  /client/company/invites             (legacy path, now takes org_role + scope)
//   POST  /client/workspace/members/{id}/role
//   POST  /client/workspace/members/{id}/deactivate
//   POST  /client/workspace/transfer-ownership
//   GET   /client/company/invites             (lists pending invites; reused)
//   DELETE /client/company/invites/{id}       (revoke)

const ORG_ROLES = [
  { value: 'admin',           label: 'Admin',          desc: 'Full access — manage staffing, billing, members' },
  { value: 'event_manager',   label: 'Event Manager',  desc: 'Manage assigned shifts & talent (scoped)' },
  { value: 'billing_manager', label: 'Billing Manager', desc: 'Invoices & payment methods only' },
  { value: 'viewer',          label: 'Viewer',         desc: 'Read-only access (scoped)' },
];

const SCOPE_TYPES = [
  { value: 'company_wide', label: 'Company-wide (all markets/events)' },
  { value: 'markets',      label: 'Specific markets' },
  { value: 'events',       label: 'Specific events / campaigns' },
  { value: 'departments',  label: 'Specific departments' },
  { value: 'regions',      label: 'Specific regions' },
  { value: 'billing_only', label: 'Billing only' },
];


const RoleBadge = ({ role }) => {
  const m = {
    owner:           { label: 'Owner',          cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: Crown },
    admin:           { label: 'Admin',          cls: 'bg-cyan-100 text-cyan-700 border-cyan-200', icon: Shield },
    event_manager:   { label: 'Event Manager',  cls: 'bg-violet-100 text-violet-700 border-violet-200', icon: Calendar },
    billing_manager: { label: 'Billing Manager', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Building2 },
    viewer:          { label: 'Viewer',         cls: 'bg-zinc-100 text-zinc-700 border-zinc-200', icon: UsersIcon },
  }[role] || { label: role, cls: 'bg-zinc-100 text-zinc-700', icon: UsersIcon };
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={`${m.cls} text-[11px]`} data-testid={`role-badge-${role}`}>
      <Icon className="w-3 h-3 mr-0.5" /> {m.label}
    </Badge>
  );
};


const ScopeChips = ({ member }) => {
  if (!member || member.scope_type === 'company_wide') return null;
  const chips = [
    ...(member.scope_markets || []).map((m) => ({ k: `m-${m}`, label: m, icon: MapPin })),
    ...(member.scope_regions || []).map((r) => ({ k: `r-${r}`, label: r, icon: MapPin })),
    ...(member.scope_departments || []).map((d) => ({ k: `d-${d}`, label: d, icon: Building2 })),
    ...(member.scope_events || []).map((e) => ({ k: `e-${e}`, label: `event:${e.slice(0,8)}`, icon: Calendar })),
  ];
  if (chips.length === 0) {
    return <span className="text-[11px] italic text-amber-700">⚠️ {member.scope_type} scope but no items assigned</span>;
  }
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {chips.map(({ k, label, icon: Icon }) => (
        <Badge key={k} variant="outline" className="text-[10px] bg-white">
          <Icon className="w-3 h-3 mr-0.5" /> {label}
        </Badge>
      ))}
    </div>
  );
};


const CompanyUsers = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState(null);   // { company, me }
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [entitlements, setEntitlements] = useState(null); // Iter-226c
  const [showInvite, setShowInvite] = useState(false);
  const [showEdit, setShowEdit] = useState(null);     // member being edited
  const [showTransfer, setShowTransfer] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastAcceptUrl, setLastAcceptUrl] = useState(null);

  // Invite form state
  const [inviteForm, setInviteForm] = useState({
    email: '', org_role: 'admin', scope_type: 'company_wide',
    markets: '', events: '', departments: '', regions: '',
  });

  // Edit-member form state (mirrors invite, plus member_id)
  const [editForm, setEditForm] = useState(null);
  const [transferTarget, setTransferTarget] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ws, mems, inv, ent] = await Promise.all([
        api.get('/client/workspace'),
        api.get('/client/workspace/members'),
        api.get('/client/company/invites'),
        api.get('/monetization/my-entitlements').catch(() => ({ data: null })),
      ]);
      setWorkspace(ws.data);
      setMembers(mems.data.members || []);
      setPending(inv.data.pending_invites || []);
      setEntitlements(ent.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load workspace');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, []);

  const canManage = useMemo(
    () => ['owner', 'admin'].includes(workspace?.me?.org_role),
    [workspace],
  );

  const splitList = (v) => (v || '').split(',').map((s) => s.trim()).filter(Boolean);

  const sendInvite = async () => {
    if (!inviteForm.email.trim()) return;
    setSending(true);
    try {
      const payload = {
        email: inviteForm.email.trim(),
        role: 'client',
        org_role: inviteForm.org_role,
        scope_type: inviteForm.scope_type,
        markets: splitList(inviteForm.markets),
        events: splitList(inviteForm.events),
        departments: splitList(inviteForm.departments),
        regions: splitList(inviteForm.regions),
      };
      const res = await api.post('/client/company/invites', payload);
      toast.success(res.data.message || 'Invite sent');
      setLastAcceptUrl(res.data.accept_url || null);
      setInviteForm({ ...inviteForm, email: '' });
      setShowInvite(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/client/company/invites/${id}`);
      toast.success('Invite revoked');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke');
    }
  };

  const startEdit = (m) => {
    setEditForm({
      member_id: m.member_id,
      email: m.email,
      org_role: m.org_role === 'owner' ? 'admin' : m.org_role,
      scope_type: m.scope_type || 'company_wide',
      markets: (m.scope_markets || []).join(', '),
      events: (m.scope_events || []).join(', '),
      departments: (m.scope_departments || []).join(', '),
      regions: (m.scope_regions || []).join(', '),
    });
    setShowEdit(m);
  };

  const saveEdit = async () => {
    if (!editForm) return;
    try {
      await api.post(`/client/workspace/members/${editForm.member_id}/role`, {
        org_role: editForm.org_role,
        scope_type: editForm.scope_type,
        markets: splitList(editForm.markets),
        events: splitList(editForm.events),
        departments: splitList(editForm.departments),
        regions: splitList(editForm.regions),
      });
      toast.success('Member updated');
      setShowEdit(null); setEditForm(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const deactivate = async (memberId) => {
    if (!window.confirm('Remove this teammate from the workspace?')) return;
    try {
      await api.post(`/client/workspace/members/${memberId}/deactivate`);
      toast.success('Member removed');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };

  const transferOwnership = async () => {
    if (!transferTarget) return;
    if (!window.confirm('Transfer ownership? You will become an Admin.')) return;
    try {
      await api.post('/client/workspace/transfer-ownership', {
        new_owner_user_id: transferTarget,
      });
      toast.success('Ownership transferred');
      setShowTransfer(false); setTransferTarget('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transfer failed');
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Copied');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-12">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-cyan-50/40 via-white to-white min-h-screen" data-testid="company-users-page">
      <div className="max-w-3xl mx-auto py-6 px-4">

        {/* Header / Workspace */}
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>

        <Card className="mb-4">
          <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="w-5 h-5 text-cyan-500" />
                <h1 className="text-xl font-bold text-gray-900" data-testid="workspace-name">
                  {workspace?.company?.name}
                </h1>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>Your role:</span>
                <RoleBadge role={workspace?.me?.org_role} />
                <span>·</span>
                <span>Scope: <strong>{workspace?.me?.scope_type?.replace('_', ' ')}</strong></span>
              </div>
            </div>
            <div className="flex gap-2">
              {canManage && (
                <Button
                  onClick={() => setShowInvite(true)}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white"
                  size="sm"
                  data-testid="open-invite-dialog"
                >
                  <UserPlus className="w-4 h-4 mr-1" /> Invite Team Member
                </Button>
              )}
              {workspace?.me?.is_owner && members.filter((m) => m.org_role === 'admin').length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowTransfer(true)}
                  data-testid="transfer-ownership-btn"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-1" /> Transfer Ownership
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {lastAcceptUrl && (
          <Card className="border-amber-200 bg-amber-50 mb-4" data-testid="invite-link-banner">
            <CardContent className="p-3 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-amber-800">Invite link ready (in case the email doesn't arrive):</p>
                <code className="text-[11px] text-amber-900 break-all block">{lastAcceptUrl}</code>
              </div>
              <Button size="sm" variant="outline" onClick={() => copyLink(lastAcceptUrl)} data-testid="copy-invite-link">
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Iter-226c — seat counter + $49 add-on prompt */}
        {entitlements?.seats && (
          <Card className="mb-4 bg-gradient-to-r from-cyan-50 to-white" data-testid="seat-counter-card">
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm">
                <p className="font-semibold text-gray-900">
                  {entitlements.plan_name} plan ·{' '}
                  <span data-testid="seats-active-count">{entitlements.seats.active}</span>
                  {entitlements.seats.limit === -1
                    ? ' / Unlimited'
                    : <> / <span data-testid="seats-limit">{entitlements.seats.limit}</span></>
                  } seats used
                  {entitlements.seats.pending_invites > 0 && (
                    <span className="text-gray-500 font-normal">
                      {' '}· {entitlements.seats.pending_invites} pending invite{entitlements.seats.pending_invites !== 1 ? 's' : ''}
                    </span>
                  )}
                </p>
                {entitlements.seats.limit !== -1 && entitlements.seats.remaining === 0 && entitlements.seats.additional_user_price_usd > 0 && (
                  <p className="text-[12px] text-amber-700 mt-1" data-testid="seat-addon-prompt">
                    Need more seats? Add additional team members for ${Number(entitlements.seats.additional_user_price_usd).toFixed(0)} each
                    {' '}— or upgrade for more included seats.
                  </p>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/client/subscription')}
                data-testid="manage-plan-btn"
              >
                Manage Plan
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Members */}
        <Card className="mb-4">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm">Team Members ({members.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0 divide-y divide-gray-100">
            {members.map((m) => (
              <div
                key={m.user_id}
                className="px-4 py-3 flex items-start justify-between gap-3"
                data-testid={`member-row-${m.user_id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                    <span className="truncate">{m.email}</span>
                    <RoleBadge role={m.org_role} />
                    {!m.is_active && <Badge variant="outline" className="text-[10px]">Inactive</Badge>}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    {m.joined_at ? `joined ${formatDate(m.joined_at)}` : ''}
                  </p>
                  <ScopeChips member={m} />
                </div>
                {canManage && m.org_role !== 'owner' && (
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => startEdit(m)}
                      data-testid={`edit-member-${m.user_id}`}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => deactivate(m.member_id)}
                      className="text-red-600 hover:bg-red-50"
                      data-testid={`remove-member-${m.user_id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Pending invites */}
        {pending.length > 0 && (
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm">Pending Invites ({pending.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0 divide-y divide-gray-100">
              {pending.map((inv) => (
                <div
                  key={inv.invite_id}
                  className="px-4 py-3 flex items-center justify-between"
                  data-testid={`pending-row-${inv.invite_id}`}
                >
                  <div>
                    <p className="text-sm font-medium text-gray-800 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" /> {inv.email}
                    </p>
                    <p className="text-[11px] text-gray-500">
                      expires {formatDate(inv.expires_at)} · role: {inv.role}
                    </p>
                  </div>
                  {canManage && (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => revoke(inv.invite_id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`revoke-invite-${inv.invite_id}`}
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Revoke
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Invite Modal */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite a team member</DialogTitle>
            <DialogDescription>
              They'll join <strong>{workspace?.company?.name}</strong> with the role and scope you pick.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="teammate@company.com"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={inviteForm.org_role}
                onValueChange={(v) => setInviteForm({ ...inviteForm, org_role: v })}
              >
                <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ORG_ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      <span className="font-medium">{r.label}</span>
                      <span className="block text-[10px] text-gray-500">{r.desc}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Scope</Label>
              <Select
                value={inviteForm.scope_type}
                onValueChange={(v) => setInviteForm({ ...inviteForm, scope_type: v })}
              >
                <SelectTrigger data-testid="invite-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SCOPE_TYPES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {inviteForm.scope_type === 'markets' && (
              <div>
                <Label>Markets (comma-separated)</Label>
                <Input
                  placeholder="Atlanta, Miami, Dallas"
                  value={inviteForm.markets}
                  onChange={(e) => setInviteForm({ ...inviteForm, markets: e.target.value })}
                  data-testid="invite-markets-input"
                />
              </div>
            )}
            {inviteForm.scope_type === 'regions' && (
              <div>
                <Label>Regions (comma-separated)</Label>
                <Input
                  placeholder="Southeast, West Coast"
                  value={inviteForm.regions}
                  onChange={(e) => setInviteForm({ ...inviteForm, regions: e.target.value })}
                />
              </div>
            )}
            {inviteForm.scope_type === 'departments' && (
              <div>
                <Label>Departments (comma-separated)</Label>
                <Input
                  placeholder="Hospitality, Production"
                  value={inviteForm.departments}
                  onChange={(e) => setInviteForm({ ...inviteForm, departments: e.target.value })}
                />
              </div>
            )}
            {inviteForm.scope_type === 'events' && (
              <div>
                <Label>Event IDs (comma-separated UUIDs)</Label>
                <Input
                  placeholder="uuid-1, uuid-2"
                  value={inviteForm.events}
                  onChange={(e) => setInviteForm({ ...inviteForm, events: e.target.value })}
                />
              </div>
            )}
            <p className="text-[11px] text-gray-400">Link expires in 7 days. The invitee inherits your company billing.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowInvite(false)}>Cancel</Button>
            <Button
              onClick={sendInvite}
              disabled={sending || !inviteForm.email.trim()}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              size="sm"
              data-testid="send-invite-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Mail className="w-4 h-4 mr-1" />}
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Modal */}
      <Dialog open={!!showEdit} onOpenChange={(v) => { if (!v) { setShowEdit(null); setEditForm(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit {editForm?.email}</DialogTitle>
            <DialogDescription>Update role and access scope for this teammate.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <div>
                <Label>Role</Label>
                <Select
                  value={editForm.org_role}
                  onValueChange={(v) => setEditForm({ ...editForm, org_role: v })}
                >
                  <SelectTrigger data-testid="edit-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ORG_ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Scope</Label>
                <Select
                  value={editForm.scope_type}
                  onValueChange={(v) => setEditForm({ ...editForm, scope_type: v })}
                >
                  <SelectTrigger data-testid="edit-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SCOPE_TYPES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editForm.scope_type === 'markets' && (
                <Input
                  placeholder="Atlanta, Miami"
                  value={editForm.markets}
                  onChange={(e) => setEditForm({ ...editForm, markets: e.target.value })}
                />
              )}
              {editForm.scope_type === 'regions' && (
                <Input
                  placeholder="Southeast, West Coast"
                  value={editForm.regions}
                  onChange={(e) => setEditForm({ ...editForm, regions: e.target.value })}
                />
              )}
              {editForm.scope_type === 'departments' && (
                <Input
                  placeholder="Hospitality, Production"
                  value={editForm.departments}
                  onChange={(e) => setEditForm({ ...editForm, departments: e.target.value })}
                />
              )}
              {editForm.scope_type === 'events' && (
                <Input
                  placeholder="event-uuid, event-uuid"
                  value={editForm.events}
                  onChange={(e) => setEditForm({ ...editForm, events: e.target.value })}
                />
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setShowEdit(null); setEditForm(null); }}>Cancel</Button>
            <Button onClick={saveEdit} size="sm" className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="save-edit-btn">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Ownership Modal */}
      <Dialog open={showTransfer} onOpenChange={setShowTransfer}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer ownership</DialogTitle>
            <DialogDescription>
              Pass company ownership to another admin. You will be demoted to Admin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>New owner</Label>
            <Select value={transferTarget} onValueChange={setTransferTarget}>
              <SelectTrigger data-testid="transfer-target-select"><SelectValue placeholder="Pick an admin..." /></SelectTrigger>
              <SelectContent>
                {members.filter((m) => m.org_role === 'admin').map((m) => (
                  <SelectItem key={m.user_id} value={m.user_id}>{m.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowTransfer(false)}>Cancel</Button>
            <Button onClick={transferOwnership} size="sm" disabled={!transferTarget} data-testid="confirm-transfer-btn">
              Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyUsers;
